import React, { useState, useEffect } from "react";
import PromotionServiceInstance from "service/promotion.service";
import ActionBar from "./components/ActionBar";
import PromotionTable from "./components/PromotionTable";
import PromotionFormModal from "./components/PromotionFormModal";
import DeleteConfirmationModal from "./components/DeleteConfirmationModal";
import HeaderSection from "components/headers-section";

const PromotionManager = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentPromotion, setCurrentPromotion] = useState({
    name_promotion: "",
    annee_promotion: new Date().getFullYear(),
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await PromotionServiceInstance.getAll();
      setPromotions(result || []);
    } catch (error) {
      setError(
        "Impossible de charger les promotions. Veuillez réessayer plus tard."
      );
      console.error("Error fetching promotions:", error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setCurrentPromotion({
      name_promotion: "",
      annee_promotion: new Date().getFullYear(),
    });
    setIsEditing(false);
    setIsFormModalOpen(true);
  };

  const openEditModal = (promotion) => {
    setCurrentPromotion({
      id_promotion: promotion.id_promotion,
      name_promotion: promotion.name_promotion,
      annee_promotion: promotion.annee_promotion,
    });
    setIsEditing(true);
    setIsFormModalOpen(true);
  };

  const openDeleteModal = (promotion) => {
    setCurrentPromotion(promotion);
    setIsDeleteModalOpen(true);
  };

  const handleSubmitForm = async (promotionData) => {
    setLoading(true);
    try {
      if (isEditing) {
        await PromotionServiceInstance.update(
          currentPromotion.id_promotion,
          promotionData
        );
      } else {
        await PromotionServiceInstance.create(promotionData);
      }

      fetchPromotions();
      setIsFormModalOpen(false);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        errors: error.response?.data?.errors || {
          general: isEditing
            ? "Impossible de modifier la promotion. Veuillez réessayer."
            : "Impossible de créer la promotion. Veuillez réessayer.",
        },
      };
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await PromotionServiceInstance.delete(currentPromotion.id_promotion);
      fetchPromotions();
      setIsDeleteModalOpen(false);
    } catch (error) {
      setError("Impossible de supprimer la promotion. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const filteredPromotions = promotions.filter(
    (promotion) =>
      promotion.name_promotion
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      promotion.annee_promotion.toString().includes(searchTerm)
  );

  return (
    <div className="min-h-screen bg-gray-50 px-5 py-8 md:px-8">
      <HeaderSection
        title="Gestion des Promotions"
        description="Créez et gérez les promotions des étudiants"
      />

      <ActionBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onRefresh={fetchPromotions}
        onCreateNew={openCreateModal}
        loading={loading}
      />

      {error && (
        <div
          className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600"
          role="alert"
        >
          {error}
        </div>
      )}

      <PromotionTable
        promotions={filteredPromotions}
        loading={loading}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      <PromotionFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSubmit={handleSubmitForm}
        initialPromotion={currentPromotion}
        isEditing={isEditing}
        loading={loading}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        promotion={currentPromotion}
        loading={loading}
      />
    </div>
  );
};

export default PromotionManager;
